import classNames from 'classnames';
import { useEffect, useState } from 'react';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import MultipleSelector, {
  DisplayLabel,
} from '@/components/molecules/multiple-selector/multiple-selector';
import Lightbox from '@/components/utilities/lightbox/lightbox';
import { useI18n } from '@/libraries/contexts/i18n-context';
import styles from './booking-extra-custom-select-modal.module.scss';

export interface BookingExtraCustomSelectModalProps {
  /** The class name to apply */
  className?: string;
  /** The description of the modal */
  description?: string;
  /** The headline of the modal */
  headline?: string;
  /** Indicates whether the modal is open. */
  isOpen?: boolean;
  /** The callback to handle the save button click. */
  onClickSave?: (
    passengers: Content.Components['GenericProductPassengerDto'][],
  ) => void;
  /** The callback to handle the close event. */
  onClose: () => void;
  /** The passengers to display in the modal */
  passengers: Content.Components['GenericProductPassengerDto'][];
}

const BookingExtraCustomSelectModal = ({
  className,
  description,
  headline,
  isOpen = false,
  onClickSave,
  onClose,
  passengers = [],
}: BookingExtraCustomSelectModalProps) => {
  const { t } = useI18n();

  const [updatedPassengers, setUpdatedPassengers] = useState<
    Content.Components['GenericProductPassengerDto'][]
  >([]);

  const handleOnClickSave = () => {
    onClickSave?.(updatedPassengers);
    onClose();
  };

  useEffect(() => {
    setUpdatedPassengers(passengers);
  }, [passengers]);

  const handleMultipleSelect = (
    selectedValue: string,
    uniquePaxHash: string,
    direction: Content.Components['ProductTransportDirection'] = 'default',
  ) => {
    const updated = updatedPassengers.map(passenger => {
      if (passenger.passenger.uniquePaxHash === uniquePaxHash) {
        const updatedRelevantProducts =
          passenger.relevantPassengerBookingProducts.map(product =>
            product.direction === direction
              ? { ...product, isSelected: product.bindId === selectedValue }
              : product,
          );

        return {
          ...passenger,
          isSelected: updatedRelevantProducts.some(
            product => product.isSelected,
          ),
          relevantPassengerBookingProducts: updatedRelevantProducts,
        };
      }

      return passenger;
    });

    setUpdatedPassengers(updated);
  };

  useEffect(() => {
    setUpdatedPassengers(passengers);
  }, [isOpen, passengers]);

  const renderMultiSelect = () => {
    return (
      <div className={styles.multipleOptionsWrapper}>
        {updatedPassengers.map(passenger => {
          return (
            <MultipleSelector
              key={passenger.passenger.uniquePaxHash}
              displayLabel={DisplayLabel.ProductAllotmentDate}
              onChange={selectedValue =>
                handleMultipleSelect(
                  selectedValue,
                  passenger.passenger.uniquePaxHash,
                )
              }
              passenger={passenger}
            />
          );
        })}
      </div>
    );
  };

  return (
    <Lightbox
      className={classNames(styles.component, className)}
      isCentered
      isFullWidthOnMobile
      isOpen={isOpen}
      onClose={onClose}
      wrapperClassName={styles.lightboxWrapper}
    >
      <div className={classNames(styles.lightboxInnerWrapper)}>
        <div className={styles.contentWrapper}>
          <div className={styles.topSection}>
            <Typography className={styles.headline} tagStyle="headingMedium">
              {headline}
            </Typography>

            {description && (
              <RichText className={styles.description} text={description} />
            )}

            {renderMultiSelect()}
          </div>

          <div className={styles.bottomSection}>
            <Button
              className={styles.cta}
              onClick={onClose}
              text={t('items.common.words.cancel')}
              variant="secondary"
            />

            <Button
              className={styles.cta}
              onClick={handleOnClickSave}
              text={t('items.modules.bookingFlow.save')}
              variant="primary"
            />
          </div>
        </div>
      </div>
    </Lightbox>
  );
};

BookingExtraCustomSelectModal.displayName = 'BookingExtraCustomSelectModal';

export default BookingExtraCustomSelectModal;
